/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Input } from "@nordcloud/gnui";
import { useDisclosure } from "~/hooks";
import { SidebarSelector } from "./SidebarSelector";
import { SidebarWithMultiSelectProps, UISidebarProps } from "./types";
import { getFirstOptionLabel, getMultiSelectLabel } from "./utils";

type Props = SidebarWithMultiSelectProps & UISidebarProps;

export function SidebarWithMultiSelect({
  options,
  name,
  pluralName,
  placeholder,
  hasSubtitle,
  hasIcon,
  onClear = () => undefined,
  onMultiSelect,
  selectedOptions,
  disabled = false,
  title,
  showLoader = false,
}: Props) {
  const [selectedItems, setSelectedItems] = React.useState(selectedOptions);

  const { isOpen, close, open } = useDisclosure();

  React.useEffect(() => {
    setSelectedItems(selectedOptions);
  }, [selectedOptions]);

  const onApply = (item: string) => {
    const newSelectedItems = [...(selectedItems ?? [])];
    const indexOfItem = newSelectedItems.indexOf(item);
    if (indexOfItem !== -1) {
      newSelectedItems.splice(indexOfItem, 1);
    } else {
      newSelectedItems.push(item);
    }
    setSelectedItems(newSelectedItems);
  };

  const onSelectAll = () => {
    setSelectedItems(options.map((item) => item.value ?? ""));
  };

  const onClearSelection = () => {
    setSelectedItems([]);
  };

  const handleMultiSelectSubmit = () => {
    onMultiSelect(selectedItems ?? []);
    close();
  };

  const selectedCount = (selectedOptions ?? []).length;

  const firstOptionLabel =
    selectedCount === 1 && getFirstOptionLabel(selectedOptions ?? [], options);

  const multiSelectLabel =
    selectedCount > 1 &&
    getMultiSelectLabel(selectedCount, name ?? "", pluralName);

  const innerValue = firstOptionLabel || multiSelectLabel || "";

  const hasMultiSelectValue = selectedCount > 0;

  return (
    <>
      <Input
        type="text"
        title={title || `Select ${name}`}
        placeholder={placeholder || `Select ${name}`}
        value={innerValue}
        icon="sidebar"
        showClearButton={hasMultiSelectValue}
        disabled={disabled}
        onClick={open}
        onClear={onClear}
      />
      <SidebarSelector
        hasIcon={hasIcon}
        name={name}
        pluralName={pluralName}
        isOpen={isOpen}
        hasSubtitle={hasSubtitle}
        options={options}
        selectedOptions={selectedItems}
        showLoader={showLoader}
        onClick={close}
        onSubmit={onApply}
        onMultiSubmit={handleMultiSelectSubmit}
        onSelectAll={onSelectAll}
        onClear={onClearSelection}
      />
    </>
  );
}
